"use client"

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saveSearchForUser, deleteSavedSearch, getUser } from '@/app/actions';
import { RecordModel } from 'pocketbase';
import { toast } from 'react-toastify';
import { usePathname } from 'next/navigation';

function SaveSearchButton({size = 'large', searchUrl}: {size?: 'small' | 'large', searchUrl?: string}) {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const url = searchUrl || pathname;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [receiveEmails, setReceiveEmails] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
  });

  const saveMutation = useMutation({
    mutationFn: saveSearchForUser,
    onMutate: async (newSearch) => {
      await queryClient.cancelQueries({ queryKey: ['user'] });
      const previousUser = queryClient.getQueryData(['user']);
      queryClient.setQueryData(['user'], (old: RecordModel | null) => {
        if (!old) return old;
        const savedSearches = old.savedSearches || [];
        return {
          ...old,
          savedSearches: [...savedSearches, newSearch],
        };
      });
      return { previousUser };
    },
    onError: (error, _, context: any) => {
      queryClient.setQueryData(['user'], context.previousUser);
      toast.error('Failed to save search. Please try again.');
    },
    onSuccess: () => {
      toast.success('Search saved successfully!');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSavedSearch,
    onMutate: async (deleteUrl) => {
      await queryClient.cancelQueries({ queryKey: ['user'] });
      const previousUser = queryClient.getQueryData(['user']);
      queryClient.setQueryData(['user'], (old: RecordModel | null) => {
        if (!old) return old;
        const savedSearches = old.savedSearches || [];
        return {
          ...old,
          savedSearches: savedSearches.filter((search: any) => search.url !== deleteUrl),
        };
      });
      return { previousUser };
    },
    onError: (error, _, context: any) => {
      queryClient.setQueryData(['user'], context.previousUser);
      toast.error('Failed to delete saved search. Please try again.');
    },
    onSuccess: () => {
      toast.success('Search deleted successfully!');
    },
  });

  const isSaved = (user && 'savedSearches' in user)
    ? user.savedSearches?.some((search: any) => search.url === url)
    : false;

  const handleClick = () => {
    if (!user) {
      document.getElementById("sign-up-modal")?.click();
    } else {
      if (isSaved) {
        deleteMutation.mutate(url);
      } else {
        setIsModalOpen(true);
      }
    }
  };

  const handleSave = () => {
    if (searchName.trim()) {
      saveMutation.mutate({ url, name: searchName, receiveEmails });
      setIsModalOpen(false);
      setSearchName('');
      setReceiveEmails(false);
    } else {
      toast.error('Please enter a name for your search.');
    }
  };

  if (isLoading) {
    return (
      <button className={`btn btn-secondary rounded-full ${size === 'small' ? 'btn-circle' : 'sm:btn-sm w-full sm:w-auto'}`} disabled>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-primary-content"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
        {size === 'large' && 'Save Search'}
      </button>
    );
  }

  return (
    <>
      <button 
        onClick={handleClick}
        className={`btn rounded-full btn-secondary text-primary-content ${size === 'small' ? 'btn-circle' : 'sm:btn-sm w-full sm:w-auto'}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-primary-content"
          fill={isSaved ? "currentColor" : "none"}
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
        {size === 'large' && (isSaved ? 'Saved' : 'Save Search')}
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Save Search</h2>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Enter a name for your search"
              className="w-full p-2 border rounded mb-4"
            />
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={receiveEmails}
                onChange={(e) => setReceiveEmails(e.target.checked)}
                className="mr-2"
              />
              Receive email updates for this search
            </label>
            <div className="flex justify-end">
              <button onClick={() => setIsModalOpen(false)} className="btn btn-ghost mr-2">Cancel</button>
              <button onClick={handleSave} className="btn btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SaveSearchButton